import { memo } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { formatCurrency } from "helpers";
import { PlatformTrends } from "services/api/platform/types";

import CardHeader from "./common/CardHeader";
import { BarTooltipRow, BarTooltipTitle } from "./common/HoverPopup";
import { primary, seriesHex } from "./common/dashboardPalette";
import CssBarChart, { CssBar } from "./CssBarChart";
import { combinationLabel, eur0, monthLabel, num } from "./format";

/** Tamni tooltip za MRR trend — recharts inače crta bijeli. */
const MrrTrendTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;

  const point = payload[0];

  return (
    <div className="bg-slate-800 text-white rounded-lg shadow-xl p-3 min-w-[160px]">
      <BarTooltipTitle>{point.payload.month}</BarTooltipTitle>
      <BarTooltipRow label="MRR" value={formatCurrency.format(point.value)} emphasis="positive" />
    </div>
  );
};

interface GrowthSectionProps {
  trends: PlatformTrends;
}

/**
 * Rast platforme.
 *
 * Paketa (Basic/Standard/Advanced) više nema — bili su izmišljeni. Klijent sjedi na
 * kombinaciji packova, pa raspodjela ide po njoj.
 */
const GrowthSection: React.FC<GrowthSectionProps> = ({ trends }) => {
  const combinationBars: CssBar[] = trends.combinations.map((item) => ({
    label: combinationLabel(item.keyCode),
    value: item.mrr,
  }));

  const newTenantBars: CssBar[] = trends.newTenants.map((point) => ({
    label: monthLabel(point.month),
    shortLabel: monthLabel(point.month),
    value: point.value,
  }));

  const mrrSeries = trends.mrr.map((point) => ({
    month: monthLabel(point.month),
    value: point.value,
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {/* MRR po kombinaciji packova */}
      <CssBarChart
        title="MRR po kombinaciji"
        subtitle="Mjesečni prihod po odabiru paketa"
        bars={combinationBars}
        formatValue={eur0}
        tooltipLabel="MRR"
        colorMode="series"
        showTopValue
        footer={
          <div className="space-y-2">
            {trends.combinations.length === 0 && (
              <p className="text-sm text-slate-400">
                Nijedna pretplata još nije potpisana.
              </p>
            )}

            {trends.combinations.map((item, index) => (
              <div key={item.keyCode} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: seriesHex(index) }}
                  />
                  <span className="text-slate-700">{combinationLabel(item.keyCode)}</span>
                  <span className="text-slate-400 text-xs">({num(item.tenantCount)})</span>
                </span>
                <span className="font-medium text-slate-900">{eur0(item.mrr)}</span>
              </div>
            ))}
          </div>
        }
      />

      {/* MRR trend */}
      <div className="rounded-sm px-4 py-5 bg-white h-full flex flex-col border border-slate-200">
        <CardHeader
          title="MRR trend"
          subtitle="Kako se prihod slagao po potpisima"
          className="mb-4"
        />

        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={mrrSeries} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={primary.hex} stopOpacity={0.3} />
                <stop offset="100%" stopColor={primary.hex} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
              width={44}
            />
            <Tooltip content={<MrrTrendTooltip />} cursor={{ stroke: "#94a3b8", strokeWidth: 1 }} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={primary.hex}
              strokeWidth={2}
              fill="url(#mrrFill)"
            />
          </AreaChart>
        </ResponsiveContainer>

        {/*
          Bez ove napomene brojka izgleda kao povijesni snimak. Povijesne cijene i broj vozila
          se nigdje ne pamte, pa je ovo današnji iznos raspoređen po datumima potpisa.
        */}
        <p className="text-xs text-slate-400 mt-2">
          Današnji mjesečni iznosi raspoređeni po datumima potpisa, ne povijesni snimak.
        </p>
      </div>

      {/* Novi klijenti */}
      <CssBarChart
        title="Novi klijenti"
        subtitle="Po mjesecima"
        bars={newTenantBars}
        formatValue={(value) => `${value}`}
        tooltipLabel="Novi klijenti"
      />
    </div>
  );
};

export default memo(GrowthSection);
